// src/admin/screens/collections/CollectionsDetailScreen.tsx
// Agent's case view: hero header (gradient icon + ref + status + days-overdue
// chip + amount), escalation guidance bar, loan money snapshot grid,
// repayment schedule table, full contact timeline, and the action buttons
// (log contact / call borrower / recommend default).
//
// The recommend-default button only shows once the loan crosses the 90-day
// threshold — earlier than that, sub-project B's automated reminders +
// agent phone calls are the correct next step. Both actions also gate on
// capabilities so read-only admins don't see buttons they can't click.
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, AlertTriangle, Phone, FileText, UserPlus } from 'lucide-react';
import Card from '@admin/components/Card';
import Button from '@admin/components/Button';
import Money from '@admin/components/Money';
import Skeleton from '@admin/components/Skeleton';
import StatusChip from '@admin/components/StatusChip';
import Chip from '@admin/components/Chip';
import { useCollectionsCase } from '@admin/hooks/useCollectionsCase';
import { useRole } from '@admin/auth/useRole';
import { LOAN_STATUS_LABEL, LOAN_STATUS_TONE } from '@admin/utils/loanStatus';
import { formatZambiaPhone } from '@admin/utils/phone';
import { formatCatDate } from '@admin/utils/dates';
import { formatKwacha } from '@admin/utils/money';
import type { Ngwee } from '@/lib/types';
import EscalationBar from './EscalationBar';
import ContactHistory from './ContactHistory';
import LogContactModal from './modals/LogContactModal';
import RecommendDefaultModal from '@admin/screens/loans/actions/RecommendDefaultModal';
import styles from './CollectionsDetailScreen.module.css';

type ModalKind = null | 'log' | 'recommend_default';

export default function CollectionsDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useRole();
  const { data, isLoading, error, refetch } = useCollectionsCase(id);
  const [modal, setModal] = useState<ModalKind>(null);

  if (isLoading) {
    return (
      <div className={styles.page}>
        {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} h={20} />)}
      </div>
    );
  }
  if (error) {
    return <div className={styles.page}><p style={{ color: 'var(--c-danger)' }}>{(error as Error).message}</p></div>;
  }
  if (!data || !id) return <div className={styles.page}><p>Case not found.</p></div>;

  const { loan, borrower, contacts, schedule } = data;
  const canLog = can('collections.log_contact');
  const canRecommendDefault = can('loans.recommend_default');
  const closeModal = (refresh: boolean) => {
    setModal(null);
    if (refresh) void refetch();
  };

  const isHighlyOverdue = loan.days_overdue >= 30;

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={() => navigate('/collections')}>
        <ChevronLeft size={14} /> Collections queue
      </button>

      <div className={styles.heroRow}>
        <div className={styles.heroLeft}>
          <div className={`${styles.heroIcon} ${isHighlyOverdue ? styles.danger : styles.warning}`}>
            <AlertTriangle size={24} strokeWidth={2} />
          </div>
          <div className={styles.heroText}>
            <div className={styles.heroChips}>
              <span className={styles.heroRef}>{loan.loan_number}</span>
              <StatusChip label={LOAN_STATUS_LABEL[loan.status]} tone={LOAN_STATUS_TONE[loan.status]} />
              <Chip tone={isHighlyOverdue ? 'danger' : 'warning'}>{loan.days_overdue} days overdue</Chip>
            </div>
            <h1 className={styles.heroTitle}>
              {borrower.display_name ?? 'Unknown borrower'} · {formatKwacha(loan.outstanding_ngwee as Ngwee, { decimals: 0 })}
            </h1>
            <p className={styles.heroSub}>
              {formatZambiaPhone(borrower.phone)}
              {loan.due_date && ` · due ${formatCatDate(loan.due_date)}`}
              {' · '}{contacts.length} contact attempt{contacts.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        <div className={styles.heroActions}>
          {borrower.phone && (
            <a href={`tel:${borrower.phone}`} className={styles.callBtn}>
              <Phone size={14} /> Call {formatZambiaPhone(borrower.phone)}
            </a>
          )}
          <Button
            variant="primary"
            leftIcon={<UserPlus size={14} />}
            onClick={() => setModal('log')}
            disabled={!canLog}
          >
            Log contact
          </Button>
          {loan.days_overdue >= 90 && (
            <Button
              variant="danger"
              leftIcon={<FileText size={14} />}
              onClick={() => setModal('recommend_default')}
              disabled={!canRecommendDefault}
            >
              Recommend default
            </Button>
          )}
        </div>
      </div>

      <EscalationBar
        daysOverdue={loan.days_overdue}
        lastContactAt={contacts[0]?.contacted_at ?? null}
      />

      <Card title="Loan snapshot">
        <div className={styles.snapshotGrid}>
          <div className={styles.statTile}>
            <p className={styles.statLabel}>Principal</p>
            <p className={styles.statValue}><Money amount={loan.principal_ngwee} /></p>
          </div>
          <div className={styles.statTile}>
            <p className={styles.statLabel}>Interest + fee</p>
            <p className={styles.statValue}>
              <Money amount={(loan.interest_ngwee + loan.fee_ngwee) as typeof loan.interest_ngwee} />
            </p>
          </div>
          <div className={styles.statTile}>
            <p className={styles.statLabel}>Total repayable</p>
            <p className={styles.statValue}><Money amount={loan.total_repayable_ngwee} /></p>
          </div>
          <div className={styles.statTile}>
            <p className={styles.statLabel}>Outstanding</p>
            <p className={`${styles.statValue} ${styles.danger}`}>
              <Money amount={loan.outstanding_ngwee} />
            </p>
          </div>
          <div className={styles.statTile}>
            <p className={styles.statLabel}>Last payment</p>
            {loan.last_payment_at ? (
              <>
                <p className={styles.statValue}>
                  <Money amount={loan.last_payment_amount_ngwee} />
                </p>
                <p className={styles.statValueSub}>{formatCatDate(loan.last_payment_at)}</p>
              </>
            ) : (
              <p className={styles.statValue} style={{ color: 'var(--c-text-muted)' }}>None</p>
            )}
          </div>
        </div>
      </Card>

      <Card title="Repayment schedule" pad="flush">
        {schedule.length === 0 ? (
          <p style={{ color: 'var(--c-text-muted)', margin: 0, padding: 24, textAlign: 'center' }}>
            No schedule generated.
          </p>
        ) : (
          <table className={styles.scheduleTable}>
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th>Due</th>
                <th>Scheduled</th>
                <th>Paid</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((s) => (
                <tr key={s.id}>
                  <td>{s.installment_no}</td>
                  <td>{formatCatDate(s.due_date)}</td>
                  <td><Money amount={s.scheduled_amount_ngwee} /></td>
                  <td><Money amount={s.paid_amount_ngwee} /></td>
                  <td><span style={{ textTransform: 'capitalize' }}>{s.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <ContactHistory contacts={contacts} />

      {modal === 'log' && (
        <LogContactModal
          loan={{ id: loan.id, loan_number: loan.loan_number }}
          onClose={closeModal}
        />
      )}
      {modal === 'recommend_default' && (
        <RecommendDefaultModal
          loan={{ id: loan.id, loan_number: loan.loan_number }}
          onClose={closeModal}
        />
      )}
    </div>
  );
}
